/**
 * Premium cartographic map themes for the BRECCS Zimbabwe School Operations Platform.
 * Advanced multi-style system with blue brand palette, glassmorphism-ready aesthetics,
 * and innovative visual layers designed for interactive school intelligence mapping.
 */

export type MapStyleId =
  | "education-atlas"
  | "azure-glass"
  | "midnight-command"
  | "clean-street"
  | "terrain-light"
  | "satellite"
  | "hybrid-light";

// ── EDUCATION ATLAS — signature default, refined blue palette ──────────────
const EDUCATION_ATLAS = [
  { elementType: "geometry", stylers: [{ color: "#F0F9FF" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2.5 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  // Water — layered sky blue
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2 }] },
  // Roads — clear hierarchy with blue tones
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#7DD3FC" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 1 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "road.highway", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#BAE6FD", weight: 0.8 }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "road.local", elementType: "geometry.stroke", stylers: [{ color: "#BAE6FD", weight: 0.4 }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { featureType: "road.local", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  // Administrative boundaries
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 1.2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "administrative", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#0C4A6E", weight: 2.5 }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 1.5 }] },
  { featureType: "administrative.locality", elementType: "geometry.stroke", stylers: [{ color: "#BAE6FD", weight: 0.8 }] },
  // Landscape
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#F0F9FF" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  // Vegetation
  { featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{ color: "#DBEAFE" }] },
  { featureType: "landscape.natural.landcover", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#F0F9FF" }] },
  // Parks
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#DBEAFE" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  // Urban POIs
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { featureType: "poi", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2 }] },
  // Schools — highlighted with blue tint
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "poi.school", elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  // Transit
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  // Buildings
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#F0F9FF" }] },
  { featureType: "building", elementType: "geometry.fill", stylers: [{ color: "#E0F2FE" }] },
  // Land parcel lines
  { featureType: "landscape.man_made", elementType: "geometry.stroke", stylers: [{ color: "#BAE6FD", weight: 0.5 }] },
];

// ── AZURE GLASS — translucent, glassmorphism-inspired premium style ──────────
const AZURE_GLASS = [
  { elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "simplified" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#7DD3FC" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2 }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 1.5 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#BAE6FD", weight: 0.5 }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#64748B" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 1 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#0C4A6E", weight: 2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#F0F9FF" }] },
  { featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#DBEAFE" }] },
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "poi", stylers: [{ visibility: "simplified" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#F1F5F9" }] },
];

// ── MIDNIGHT COMMAND — dark command-center style for premium dashboards ─────
const MIDNIGHT_COMMAND = [
  { elementType: "geometry", stylers: [{ color: "#082F49" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#082F49", weight: 3 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#7DD3FC" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "simplified" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#7DD3FC" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#082F49", weight: 2 }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 1 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#7DD3FC" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#082F49" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#0C4A6E", weight: 0.5 }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#082F49" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 1 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#7DD3FC", weight: 2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#7DD3FC" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#082F49" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#0369A1" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#7DD3FC" }] },
  { featureType: "poi", stylers: [{ visibility: "simplified" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#0C4A6E" }] },
];

// ── CLEAN STREET — minimal modern street map ─────────────────────────────────
const CLEAN_STREET = [
  { elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 0.8 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#E0F2FE", weight: 0.5 }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#64748B" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#94A3B8" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#BAE6FD", weight: 1 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#FAFCFF" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#F0F9FF" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.school", stylers: [{ visibility: "on" }] },
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#DBEAFE" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
];

// ── TERRAIN LIGHT — bright topographic with elevation hints ─────────────────
const TERRAIN_LIGHT = [
  { elementType: "geometry", stylers: [{ color: "#F0F9FF" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2.5 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#7DD3FC" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 0.8 }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#BAE6FD", weight: 0.6 }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#0EA5E9", weight: 1.2 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#0C4A6E", weight: 2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#F0F9FF" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry.fill", stylers: [{ color: "#7DD3FC" }] },
  { featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{ color: "#DBEAFE" }] },
  { featureType: "landscape.natural.landcover", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#DBEAFE" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "poi", stylers: [{ visibility: "simplified" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#E0F2FE" }] },
];

// ── HYBRID LIGHT — satellite with clean labels ──────────────────────────────
const HYBRID_LIGHT = [
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3.5 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ visibility: "simplified" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#ffffff", weight: 1 }] },
  { featureType: "road.highway", elementType: "geometry.fill", stylers: [{ color: "#7DD3FC", weight: 2 }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#ffffff", weight: 1 }] },
  { featureType: "road.arterial", elementType: "geometry.fill", stylers: [{ color: "#ffffff", weight: 1 }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#ffffff", weight: 1.5 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#ffffff", weight: 2.5 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "administrative", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 4 }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "poi.school", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
  { featureType: "landscape", elementType: "labels.text.fill", stylers: [{ color: "#0C4A6E" }] },
  { featureType: "landscape", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
];

export const MAP_STYLES: Record<MapStyleId, { label: string; description: string; styles: any[]; mapTypeId?: string }> = {
  "education-atlas": { label: "Education Atlas", description: "Signature blue platform map with school focus", styles: EDUCATION_ATLAS },
  "azure-glass": { label: "Azure Glass", description: "Translucent glassmorphism-inspired premium style", styles: AZURE_GLASS },
  "midnight-command": { label: "Midnight Command", description: "Dark command-center theme for dashboards", styles: MIDNIGHT_COMMAND },
  "clean-street": { label: "Clean Street", description: "Minimal modern street map", styles: CLEAN_STREET },
  "terrain-light": { label: "Terrain Light", description: "Bright topographic with elevation hints", styles: TERRAIN_LIGHT },
  "satellite": { label: "Satellite", description: "Natural high-resolution imagery", styles: [], mapTypeId: "satellite" },
  "hybrid-light": { label: "Hybrid Light", description: "Satellite with clean roads and labels", styles: HYBRID_LIGHT, mapTypeId: "hybrid" },
};

// Zimbabwe center — Harare
export const ZIMBABWE_CENTER = { lat: -17.8322, lng: 31.0501 };

// Default map options — Education Atlas, centered on Zimbabwe
export const LIGHT_MAP_OPTIONS = {
  center: ZIMBABWE_CENTER,
  zoom: 11,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  styles: EDUCATION_ATLAS,
  backgroundColor: "#F0F9FF",
  gestureHandling: "greedy" as const,
  mapTypeId: "roadmap",
  disableDefaultUI: true,
  zoomControl: true,
  zoomControlOptions: { position: 8 }, // RIGHT_BOTTOM
  minZoom: 6,
  maxZoom: 20,
};

// School type icon colors for markers — blue brand palette
export const SCHOOL_TYPE_COLORS: Record<string, string> = {
  primary: "#0EA5E9",
  secondary: "#0C4A6E",
  nursery: "#7DD3FC",
  vocational: "#0369A1",
  tertiary: "#082F49",
  "special needs": "#475569",
};

export const OWNERSHIP_RING_COLORS: Record<string, string> = {
  government: "#0C4A6E",
  private: "#0EA5E9",
  community: "#7DD3FC",
  "faith-based": "#0369A1",
  ngo: "#475569",
};
